/**
 * lib/fieldSpecs.js — Phase E v8c.11 (2026-02-28).
 *
 * Frontend loader for the canonical field-spec registry living at
 * `/app/shared/field_specs.json`. The backend
 * (`modules/field_specs.py`) loads the SAME file so a U17 64×25 m
 * pitch is identical on every layer of the stack. This file is the
 * SINGLE SOURCE OF TRUTH for field dimensions — the legacy
 * `lib/fieldProfiles.js` registry has been DELETED.
 *
 * Implementation note · `/app/frontend/src/shared/field_specs.json`
 * is a SYMLINK to `/app/shared/field_specs.json` so the React bundler
 * (CRA's `ModuleScopePlugin`) can include it via a relative path
 * without leaving the `src/` jail. Relative paths work in BOTH
 * webpack (dev/prod build) and Jest (no alias resolution needed).
 *
 * Future AI calibration plug-ins (SoccerNet, TS-Calib) MUST read their
 * metric values from this registry — never hard-code dimensions.
 */
import REGISTRY from "../shared/field_specs.json";

export const FIELD_SPEC_VERSION = REGISTRY.version || "0.0.0";

/** Canonical default profile id (e.g. "Adult_Outdoor"). Falls back
 *  to the first profile in the registry if no explicit default. */
export const DEFAULT_PROFILE_ID = (() => {
  const explicit = REGISTRY.default_profile_id;
  if (typeof explicit === "string" && REGISTRY.profiles?.[explicit]) {
    return explicit;
  }
  return Object.keys(REGISTRY.profiles || {})[0] || "";
})();

/** All registered profile IDs (e.g. ["Adult_Outdoor", "U17_Outdoor", …]). */
export function listProfileIds() {
  return Object.keys(REGISTRY.profiles || {});
}

/** Returns every profile object. */
export function listProfiles() {
  return Object.values(REGISTRY.profiles || {});
}

/** Resolve a profile by its canonical ID. Returns `null` on miss.
 *  Callers that want a fallback should use `DEFAULT_PROFILE_ID`
 *  explicitly so the "unknown profile" case stays visible at the
 *  call site. */
export function getProfile(profileId) {
  return (REGISTRY.profiles || {})[profileId] || null;
}

/** True when `profileId` is a real registry key (no fallback). */
export function isValidProfileId(profileId) {
  return !!(profileId && (REGISTRY.profiles || {})[profileId]);
}

/** Filter the registry by upper-level format ("Outdoor" | "Beach" |
 *  "Indoor"). Step1 dropdown narrows by this filter after the Format
 *  radio has been picked. */
export function listProfilesForFormat(format) {
  const target = (format || "").toLowerCase();
  return listProfiles().filter(
    (p) => (p.format || "").toLowerCase() === target,
  );
}

/** Sloppy lookup: pick the profile whose (format, age_group) match,
 *  with format-only fallback via `defaults[<format>]`. Matches the
 *  backend `resolve_profile` exactly so any UI choice maps to the
 *  same record on both sides of the stack. */
export function resolveProfile({ format, ageGroup } = {}) {
  const profiles = REGISTRY.profiles || {};
  if (format && ageGroup) {
    for (const p of Object.values(profiles)) {
      if ((p.format || "").toLowerCase() === format.toLowerCase()
          && (p.age_group || "").toLowerCase() === ageGroup.toLowerCase()) {
        return p;
      }
    }
  }
  if (format) {
    const defaults = REGISTRY.defaults || {};
    const fallbackId = defaults[format.toLowerCase()];
    if (fallbackId) return profiles[fallbackId] || null;
  }
  return null;
}

/** Convert a profile registry entry into the per-sequence
 *  `game_config` shape that the wizard's Step 1 form populates. */
export function toGameConfig(profileId) {
  const p = getProfile(profileId);
  if (!p) return null;
  return {
    format: p.format,
    gender: "any",
    age_group: p.age_group,
    field_length_m: p.field_length_m,
    field_width_m: p.field_width_m,
    endzone_depth_m: p.endzone_depth_m,
    brick_distance_m: p.brick_distance_m,
  };
}

/** Just the metric dimensions — handy for the calibration solver +
 *  `presetDisplayDimsFor` callsites that don't need labels. */
export function dimensions(profileId) {
  const p = getProfile(profileId);
  if (!p) return null;
  return {
    field_length_m: p.field_length_m,
    field_width_m: p.field_width_m,
    endzone_depth_m: p.endzone_depth_m,
    brick_distance_m: p.brick_distance_m,
  };
}
